import type { FilterSchema } from '@/shared/components/filter/validation'
import type { BIResponse } from '@/shared/generated'
import type { WidgetPeriods } from '../types'

import { any, last, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useBIAnalyticQuery, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Skeleton } from '@/shared/components/ui/skeleton'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'

const METRIC = 'accepted_total'

type MetricRow = Record<string, number | undefined>

const selectMetricValue = (data: BIResponse | null) => {
	if (!data?.data?.length) {
		return undefined
	}

	return prop(METRIC, last(zipSeries<MetricRow>(data.meta, data.data))) as
		number | undefined
}

const getDeltaPercent = (main: number, compared: number) => {
	if (compared === 0) {
		return main > 0 ? 100 : 0
	}

	return (main * 100) / compared - 100
}
export default function NumericAcceptedPriceWidget({
	commonFilters = [],
	commonPeriods,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const currency = useSelectedCurrency()
	const biCommonQuery = {
		currency,
		metrics: [METRIC],
		dimensions: [],
		filters: commonFilters,
		date_aggregation: null,
		label: 'NumericAcceptedPrice',
	}
	const biComparedPeriodQuery = useBIAnalyticQuery<number | undefined>(
		{
			...commonPeriods?.compared,
			...biCommonQuery,
		},
		{
			select: selectMetricValue,
			enabled: !!commonPeriods?.compared,
		}
	)
	const biMainPeriodQuery = useBIAnalyticQuery<number | undefined>(
		{
			...commonPeriods?.main,
			...biCommonQuery,
		},
		{
			select: selectMetricValue,
			enabled: !!commonPeriods?.main,
		}
	)
	const isLoading = any(prop('isLoading'), [
		biMainPeriodQuery,
		biComparedPeriodQuery,
	])
	const isError = any(prop('isError'), [
		biMainPeriodQuery,
		biComparedPeriodQuery,
	])
	const mainValue = biMainPeriodQuery.data
	const comparedValue = biComparedPeriodQuery.data
	const hasValuesToCompare = !!mainValue && !!comparedValue

	return (
		<Widget className='relative truncate'>
			<WidgetHeader
				title={t('widget.accepted_price.title', {
					defaultValue: 'Accepted Price',
				})}
				tooltip={t('widget.accepted_price.tooltip', {
					defaultValue: 'widget.accepted_price.tooltip',
				})}
				subtitle={t('widget.accepted_price.description', {
					defaultValue: 'widget.accepted_price.description',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='truncate'>
				{isLoading && (
					<div className='space-y-1'>
						<Skeleton className='col-span-2 h-6 w-24' />
						<Skeleton className='col-span-2 h-8 w-32' />
					</div>
				)}
				{mainValue && !isError && !isLoading && (
					<div className='space-y-1'>
						{hasValuesToCompare && (
							<PercentDifferenceBadge
								value={getDeltaPercent(mainValue ?? 0, comparedValue ?? 0)}
							/>
						)}
						<p className='truncate text-3xl font-semibold'>
							{formatNumber(mainValue ?? 0, NumberFormats.Compact)}
						</p>
					</div>
				)}
				{!mainValue && !isError && !isLoading && (
					<WidgetEmptyState size='small' />
				)}
			</WidgetContent>
		</Widget>
	)
}
